import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChartBar,
  ChartLineUp,
  Handshake,
  ListChecks,
  Package,
  Percent,
  ShareNetwork,
  Star,
  TrendUp,
  WarningOctagon,
  Warning,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { ConsignmentReport } from "@/components/ConsignmentReport";
import { LowStockReport } from "@/components/LowStockReport";
import { StockOnHandReport } from "@/components/reports/StockOnHandReport";
import { StockDetailReport } from "@/components/reports/StockDetailReport";
import { MarginReport } from "@/components/reports/MarginReport";
import { SalesReport } from "@/components/reports/SalesReport";
import { SalesForecastReport } from "@/components/reports/SalesForecastReport";
import { ReferralsReport } from "@/components/reports/ReferralsReport";
import { OutOfStockReport } from "@/components/reports/OutOfStockReport";
import { BenchmarkReport } from "@/components/reports/BenchmarkReport";

const REPORTS = [
  { id: "stock", label: "Stock on hand", hint: "Sq ft & value by color", icon: Package, group: "Inventory" },
  { id: "stock-detail", label: "Stock Detail", hint: "Every item: cost, sell, order status & tips", icon: ListChecks, group: "Inventory" },
  { id: "low-stock", label: "Low stock", hint: "Colors & sinks running low", icon: Warning, group: "Inventory" },
  { id: "out-of-stock", label: "Out of stock", hint: "Nothing left to sell", icon: WarningOctagon, group: "Inventory" },
  { id: "consignment", label: "Consignment", hint: "Supplier-owned stock & payouts", icon: Handshake, group: "Inventory" },
  { id: "sales", label: "Sales", hint: "By month, material, customer & person", icon: ChartLineUp, group: "Sales" },
  { id: "sales-forecast", label: "Sales Forecast", hint: "Expected sales by week or day", icon: TrendUp, group: "Sales" },
  { id: "referrals", label: "Referrals", hint: "Sales volume by referring account", icon: ShareNetwork, group: "Sales" },
  { id: "margin", label: "Margin", hint: "Revenue vs job costs", icon: Percent, group: "Money" },
  { id: "benchmark", label: "Business Benchmark", hint: "EBITDA, DSO, per-employee & more", icon: ChartBar, group: "Money" },
];

const FAV_KEY = "bgw_report_favs";
const loadFavs = () => {
  try {
    const v = JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch (err) {
    return [];
  }
};

const Self = ({ children }) => <div className="[&>section]:mt-0 [&>div]:mt-0">{children}</div>;

const BODY = {
  stock: () => <StockOnHandReport />,
  "stock-detail": () => <StockDetailReport />,
  "low-stock": () => <Self><LowStockReport /></Self>,
  "out-of-stock": () => <OutOfStockReport />,
  consignment: () => <Self><ConsignmentReport /></Self>,
  sales: () => <SalesReport />,
  "sales-forecast": () => <SalesForecastReport />,
  referrals: () => <ReferralsReport />,
  margin: () => <MarginReport />,
  benchmark: () => <BenchmarkReport />,
};

const ReportTile = ({ r, on, fav, onPick, onFav, pfx }) => {
  const Icon = r.icon;
  return (
    <div
      className={`group flex shrink-0 items-center rounded-lg border transition-colors duration-150 ${
        on
          ? "border-yellow-500/50 bg-yellow-500/10 text-white"
          : "border-transparent text-zinc-400 hover:border-zinc-800 hover:bg-zinc-900 hover:text-zinc-200"
      }`}
    >
      <button
        type="button"
        data-testid={`${pfx}report-nav-${r.id}`}
        data-active={on ? "true" : "false"}
        onClick={onPick}
        className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2 text-left"
      >
        <Icon size={16} weight={on ? "fill" : "bold"} className={on ? "text-yellow-400" : ""} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{r.label}</span>
          <span className="hidden text-[11px] text-zinc-500 lg:block">{r.hint}</span>
        </span>
      </button>
      <button
        type="button"
        data-testid={`${pfx}report-fav-${r.id}`}
        aria-pressed={fav}
        title={fav ? "Unpin from favourites" : "Pin to favourites"}
        onClick={onFav}
        className={`mr-2 rounded p-1 transition-colors ${fav ? "text-yellow-400" : "text-zinc-700 hover:text-zinc-300"}`}
      >
        <Star size={14} weight={fav ? "fill" : "bold"} />
      </button>
    </div>
  );
};

export default function InventoryReport() {
  const [params, setParams] = useSearchParams();
  const [favs, setFavs] = useState(loadFavs);
  const current = REPORTS.some((r) => r.id === params.get("report")) ? params.get("report") : "stock";
  const Body = BODY[current];
  const groups = [...new Set(REPORTS.map((r) => r.group))];
  const favReports = favs.map((id) => REPORTS.find((r) => r.id === id)).filter(Boolean);

  const toggleFav = (id) => {
    const next = favs.includes(id) ? favs.filter((f) => f !== id) : [...favs, id];
    setFavs(next);
    localStorage.setItem(FAV_KEY, JSON.stringify(next));
  };

  const section = (title, list, pfx = "", testId) => (
    <div key={title} className="mb-4" data-testid={testId}>
      <div className="mb-1.5 flex items-center gap-1.5 px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
        {pfx && <Star size={10} weight="fill" className="text-yellow-500" />}
        {title}
      </div>
      <div className="flex max-w-full gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
        {list.map((r) => (
          <ReportTile
            key={r.id}
            r={r}
            pfx={pfx}
            on={r.id === current}
            fav={favs.includes(r.id)}
            onPick={() => setParams({ report: r.id })}
            onFav={() => toggleFav(r.id)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Reporting" />
      <main className="relative z-10 mx-auto max-w-7xl px-4 pb-24 pt-6">
        <div className="text-xs uppercase tracking-widest text-zinc-600">Reporting</div>
        <div className="mt-4 grid min-w-0 gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
          <nav data-testid="report-nav" className="min-w-0 max-w-full lg:sticky lg:top-20 lg:self-start">
            {favReports.length > 0 && section("Favourites", favReports, "fav-", "report-favourites")}
            {groups.map((g) => section(g, REPORTS.filter((r) => r.group === g)))}
            {favReports.length === 0 && (
              <div className="mb-4 px-2 text-[11px] text-zinc-600" data-testid="report-fav-hint">
                Tap <Star size={10} weight="bold" className="inline" /> on any report to pin it to the top.
              </div>
            )}
            <div className="mt-2 flex flex-col gap-1 px-2 text-xs">
              <Link to="/inventory/slabs" className="text-yellow-400 hover:underline" data-testid="report-slabs-link">Manage slabs</Link>
              <Link to="/inventory/remnants" className="text-yellow-400 hover:underline" data-testid="report-remnants-link">Manage remnants</Link>
            </div>
          </nav>
          <div data-testid="report-body" className="min-w-0">
            {Body ? <Body /> : <ChartBar size={28} className="text-zinc-700" />}
          </div>
        </div>
      </main>
    </div>
  );
}
